import { useEffect, useState } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { useSearchParams } from 'react-router-dom';

import { CLEAR_VALUE, TIMEZONES } from '@/constants';

/** `TIMEZONES` must be either string[] **or** { value:string ; label:string }[] */
type TZ = (typeof TIMEZONES)[number] extends string
  ? string
  : { value: string; label: string };

type Props = {
  value?: string | null; // controlled selection
  disabled?: boolean;
  paramKey?: string; // URL param key (default "tz")
  className?: string;
  placeholder?: string;
  onChange?: (tz: string | null) => void;
};

export default function TimeZoneDropdown({
  value,
  disabled,
  paramKey = 'tz',
  className,
  placeholder = 'Time zone',
  onChange,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  /* local state for uncontrolled mode */
  const [selected, setSelected] = useState<string | null>(value ?? null);

  /* ───────────────────── initialise from URL ───────────────────── */
  useEffect(() => {
    if (value || selected) return; // already controlled / set
    const inUrl = searchParams.get(paramKey);
    if (!inUrl) return;

    setSelected(inUrl);
    onChange?.(inUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, value, selected, paramKey]);

  /* ─────────── keep URL in sync when parent drives the value ────── */
  useEffect(() => {
    if (value == null) return; // parent not controlling
    const current = searchParams.get(paramKey);
    if (current === value) return;

    const next = new URLSearchParams(searchParams);
    next.set(paramKey, value);
    setSearchParams(next, { replace: true });
    setSelected(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, paramKey]);

  /* ───────────────── helper invoked by <Dropdown> ───────────────── */
  const commit = (tz: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (tz && tz !== CLEAR_VALUE) {
      next.set(paramKey, tz);
    } else {
      next.delete(paramKey);
    }
    setSearchParams(next, { replace: true });

    setSelected(tz);
    onChange?.(tz);
  };

  /* ───────────────────── derive dropdown props ──────────────────── */
  const optsAreStrings = typeof TIMEZONES[0] === 'string';
  const dropdownValue = value ?? selected;

  return (
    <Dropdown
      value={dropdownValue}
      options={TIMEZONES as TZ[]}
      optionLabel={optsAreStrings ? undefined : 'label'}
      optionValue={optsAreStrings ? undefined : 'value'}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      showClear
      filter
      virtualScrollerOptions={{ itemSize: 32 }}
      scrollHeight="300px"
      onChange={(e) => commit(e.value ?? null)}
    />
  );
}
